/**
 * Layer State Management System Types
 * 
 * This file defines the type system for controlling the rendering, interaction,
 * and display of UI layers in the portfolio website. Each layer can be independently
 * controlled for optimal performance and user experience.
 */

import { LayerId } from '../styles/styles-data/zIndex';

/**
 * State properties for each layer
 */
export interface LayerState {
  /** Whether the layer should be mounted in the DOM */
  rendered: boolean;
  /** Whether the layer should respond to user interactions */
  interactive: boolean;
  /** Whether the layer should be visually displayed */
  displayed: boolean;
}

/**
 * Complete state mapping for all layers
 */
export type LayerActivationMap = Record<LayerId, LayerState>;

/**
 * Predefined layer state presets for common UI scenarios
 */
export interface LayerPresets {
  /** Normal browsing state - main content and navigation active */
  DEFAULT: LayerActivationMap;
  /** Project overlay is shown - main content visible but non-interactive */
  OVERLAY_ACTIVE: LayerActivationMap;
  /** Mobile menu is open - main content hidden, navigation active */
  MOBILE_MENU_ACTIVE: LayerActivationMap;
  /** Loading state - content visible but non-interactive, loading UI active */
  LOADING: LayerActivationMap;
}

/**
 * Valid preset identifiers
 */
export type PresetId = keyof LayerPresets;

/**
 * Layer state store interface
 */
export interface LayerStore {
  /** Current layer activation states */
  layerActivation: LayerActivationMap;
  /** Currently active preset */
  currentPreset: PresetId;
  /** Apply a preset to all layers */
  applyPreset: (presetId: PresetId) => void;
  /** Update a specific layer's state */
  setLayerState: (layerId: LayerId, state: Partial<LayerState>) => void;
  /** Get current state for a specific layer */
  getLayerState: (layerId: LayerId) => LayerState;
  /** Activate a layer (rendered: true, interactive: true, displayed: true) */
  activateLayer: (layerId: LayerId) => void;
  /** Deactivate a layer (all properties: false) */
  deactivateLayer: (layerId: LayerId) => void;
  /** Make layer non-interactive but keep visible */
  disableLayerInteraction: (layerId: LayerId) => void;
  /** Hide layer but keep mounted and interactive */
  hideLayer: (layerId: LayerId) => void;
  /** Show previously hidden layer */
  showLayer: (layerId: LayerId) => void;
}

/**
 * Hook return type for individual layer management
 */
export interface LayerStateHook {
  /** Current state of the layer */
  state: LayerState;
  /** Whether layer is fully active (all properties true) */
  isActive: boolean;
  /** Whether layer is visible (displayed: true) */
  isVisible: boolean;
  /** Whether layer is interactive */
  isInteractive: boolean;
  /** Whether layer is rendered in DOM */
  isRendered: boolean;
  /** Activate the layer completely */
  activate: () => void;
  /** Deactivate the layer completely */
  deactivate: () => void;
  /** Update specific properties */
  setState: (state: Partial<LayerState>) => void;
  /** Make non-interactive but keep visible */
  disableInteraction: () => void;
  /** Hide layer but keep mounted */
  hide: () => void;
  /** Show previously hidden layer */
  show: () => void;
}

/**
 * Hook return type for global layer management
 */
export interface GlobalLayerStateHook {
  /** Currently active preset */
  currentPreset: PresetId;
  /** Apply a preset to all layers */
  applyPreset: (presetId: PresetId) => void;
  /** Get current preset */
  getCurrentPreset: () => PresetId;
  /** Convenience methods for common presets */
  showDefault: () => void;
  showOverlay: () => void;
  showMobileMenu: () => void;
  showLoading: () => void;
  /** Direct layer access */
  getLayer: (layerId: LayerId) => LayerState;
  setLayer: (layerId: LayerId, state: Partial<LayerState>) => void;
}

/**
 * Component props for LayerWrapper components
 */
export interface LayerWrapperProps {
  /** Layer ID to control */
  layerId: LayerId;
  /** Child components */
  children: React.ReactNode;
  /** Optional className to add when layer is active */
  activeClassName?: string;
  /** Optional className to add when layer is inactive */
  inactiveClassName?: string;
  /** Custom render function based on layer state */
  render?: (state: LayerState) => React.ReactNode;
}

/**
 * Default layer states for initialization
 */
export const DEFAULT_LAYER_STATE: LayerState = {
  rendered: true,
  interactive: true,
  displayed: true,
};

/**
 * Inactive layer state
 */
export const INACTIVE_LAYER_STATE: LayerState = {
  rendered: false,
  interactive: false,
  displayed: false,
};

/**
 * Visible but non-interactive layer state
 */
export const DISABLED_LAYER_STATE: LayerState = {
  rendered: true,
  interactive: false,
  displayed: true,
};

/**
 * Hidden but interactive layer state (for offscreen elements)
 */
export const HIDDEN_LAYER_STATE: LayerState = {
  rendered: true,
  interactive: true,
  displayed: false,
};